import React, { useState, useEffect, PropsWithChildren } from 'react';
import styled, { css, DefaultTheme } from 'styled-components/macro';
import { Box, Layer } from 'grommet';
import { colorValue } from '../../../../lib/theme';
import { SidebarContextProvider } from './SidebarContext';
import { voidF } from '../../../../lib/functions';
import { isSmall, isLargeOrSmaller } from '../../../../lib/screen';
import { createSmokeTestAttr, SMOKE_TESTS_ELEMENTS } from '@sdxcorp/sdx-dds-gui-lib';

interface Props {
  size: string;
  width: string;
  visible?: boolean;
  onLinkClick?: () => void;
  setSidebarVisible?: (visible: boolean) => void;
  userSetVisible?: boolean | undefined;
}

const baseStyles = (width: string, theme: DefaultTheme) => css`
  z-index: 5;
  min-width: ${width};
  max-width: ${width};
  background-color: ${colorValue(theme, c => c['grey-80'])};
  overflow-y: auto;
  border-radius: 0;
  padding: 16px 0;
`;

const StyledBox = styled(Box).attrs(({ testId }: { testId?: string }) => createSmokeTestAttr({ element: SMOKE_TESTS_ELEMENTS.container, testId }))<{
  width: string;
  testId?: string;
}>`
  ${props => baseStyles(props.width, props.theme)}
`;

const StyledLayer = styled(Layer).attrs(({ testId }: { testId?: string }) =>
  createSmokeTestAttr({ element: SMOKE_TESTS_ELEMENTS.container, testId }),
)<{
  width: string;
  testId?: string;
}>`
  ${props => baseStyles(props.width, props.theme)}
`;

const Sidebar: React.FC<PropsWithChildren<Props>> = ({
  visible = true,
  userSetVisible = true,
  size,
  width,
  onLinkClick = voidF,
  setSidebarVisible = voidF,
  children,
}) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const isSmallVal = isSmall(size);
  const isLargeOrSmallerVal = isLargeOrSmaller(size);
  useEffect(() => {
    if (!isLargeOrSmallerVal && userSetVisible) {
      setSidebarVisible(true);
    } else {
      setSidebarVisible(false);
    }
  }, [isLargeOrSmallerVal, userSetVisible, setSidebarVisible]);
  if (isSmallVal && !visible) {
    return null;
  }
  return (
    <SidebarContextProvider
      sidebarExpanded={visible}
      setItemExpanded={id => setExpandedItems([id])}
      toggleItemExpanded={id => {
        if (!visible) {
          setSidebarVisible(true);
        }
        setExpandedItems(expandedItems.includes(id) ? expandedItems.filter(s => s !== id) : expandedItems.concat([id]));
      }}
      expandedItems={expandedItems}
      onLinkClick={() => {
        if (visible && (isLargeOrSmallerVal || (!isLargeOrSmallerVal && !userSetVisible))) {
          setSidebarVisible(false);
        }
        onLinkClick();
      }}
    >
      {isSmallVal ? (
        <StyledLayer testId="styledLayer" width="auto" animate={false} modal={false} margin={{ top: '64px' }} position="left" full responsive={false}>
          {children}
        </StyledLayer>
      ) : (
        <StyledBox testId="styledBox" width={visible ? width : '68px'}>
          {children}
        </StyledBox>
      )}
    </SidebarContextProvider>
  );
};

export default Sidebar;
